import * as React from 'react'
import {
  NODE_PROPERTY_SCHEMAS,
  type NodeLabel,
  type PropertySchema
} from '@atbash-labs/ontology-core'
import { Badge } from '../../../components/ui/badge'
import { SectionHeader } from './SectionHeader'

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

export function formatTypedProperty(value: unknown, schema: PropertySchema): string[] {
  if (isEmpty(value)) return []
  if (schema.type === 'boolean') return [value === true ? 'Yes' : 'No']
  if (schema.type === 'array') {
    if (!Array.isArray(value)) return [String(value)]
    return value.map((item) =>
      item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item)
    )
  }
  if (schema.type === 'object') return [JSON.stringify(value)]
  return [String(value)]
}

const LONG_TEXT_CHARS = 160
/** Roughly what fits in a badge inside a 180px grid column before truncation. */
const LONG_ITEM_CHARS = 24

/**
 * Values that would be unreadable in a grid cell — multi-line text such as a
 * parsed program listing, or lists of long lines — get a full-width block.
 */
export function isLongFormProperty(values: string[], schema: PropertySchema): boolean {
  if (schema.enum) return false
  if (schema.type === 'array') return values.some((value) => value.length > LONG_ITEM_CHARS)
  if (schema.type !== 'string') return false
  const text = values[0] ?? ''
  return text.includes('\n') || text.length > LONG_TEXT_CHARS
}

/**
 * Schema-declared properties for a generic entity, rendered with the same
 * specs-grid treatment the bespoke pages use (`SpecsSection` on Equipment):
 * one card, auto-fill columns, tiny uppercase keys. Enum values and array items render as badges so
 * multi-valued fields don't collapse into comma soup.
 */
export function TypedPropertiesSection({
  label,
  entity
}: {
  label: NodeLabel
  entity: Record<string, unknown>
}): React.ReactElement | null {
  const schema = NODE_PROPERTY_SCHEMAS[label]
  const rows = Object.entries(schema.properties)
    .map(([key, propertySchema]) => ({
      key,
      schema: propertySchema,
      values: formatTypedProperty(entity[key], propertySchema)
    }))
    .filter((row) => row.values.length > 0)

  if (rows.length === 0) return null
  const gridRows = rows.filter((row) => !isLongFormProperty(row.values, row.schema))
  const longRows = rows.filter((row) => isLongFormProperty(row.values, row.schema))
  return (
    <section>
      <SectionHeader label="Properties" subtitle={`${rows.length}`} />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(11.25rem,1fr))]">
          {gridRows.map(({ key, schema: propertySchema, values }) => (
            <div key={key} className="min-w-0">
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {key}
              </div>
              {propertySchema.enum || propertySchema.type === 'array' ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {values.map((value, index) => (
                    <Badge key={`${value}-${index}`} variant="outline" className="max-w-full">
                      <span className="truncate">{value}</span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div
                  className={
                    propertySchema.type === 'number'
                      ? 'mt-0.5 break-words font-mono text-13 font-medium text-ink tabular-nums'
                      : 'mt-0.5 break-words text-13 font-medium text-ink'
                  }
                >
                  {values[0]}
                </div>
              )}
            </div>
          ))}
        </div>
        {longRows.map(({ key, values }) => (
          <div key={key} className={gridRows.length > 0 ? 'mt-4 min-w-0' : 'min-w-0'}>
            <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
              {key}
            </div>
            <pre className="mt-1 max-h-80 overflow-auto whitespace-pre rounded-md border bg-muted/40 p-3 font-mono text-12 leading-5 text-ink">
              {values.join('\n')}
            </pre>
          </div>
        ))}
      </div>
    </section>
  )
}
